import { PersonOutline } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import * as colors from '@mui/material/colors';
import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { maskPhone } from 'services/mask';
import { nonNullish } from 'services/helper';
import { UserAndContact } from 'types/UsersAndContact.types';
import FireIcon from './FireIcon';
import MedicalIcon from './MedicalIcon';
import PanicIcon from './PanicIcon';
import ViolationIcon from './ViolationIcon';
import RequirePermission from 'components/RequirePermission';

interface MountDescriptionParam {
  applyMask?: (value: string) => string;
  value: string | null | undefined;
}

interface CommonListProps {
  items: UserAndContact[];
  mountItemName: (item: UserAndContact) => React.ReactNode;
  onEdit?: (item: UserAndContact) => void;
  onDelete?: (item: UserAndContact) => void;
}

function CommonList({ items, onEdit, onDelete, mountItemName }: CommonListProps) {
  const theme = useTheme();

  const mountItemDescription = useCallback((values: MountDescriptionParam[]): string => {
    return values
      .filter((item) => nonNullish(item.value))
      .map((item) => {
        return item.applyMask?.(item.value!) ?? item.value ?? '';
      })
      .filter(Boolean)
      .join(', ');
  }, []);

  return (
    <List
      sx={{
        flex: '1 1 auto',
        p: 0,
        mt: 2,
        overflow: 'auto',
      }}
    >
      {items.map((item, index) => (
        <Fragment key={item.id}>
          <ListItem
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                button: {
                  display: 'inline-flex',
                },
              },
            }}
            secondaryAction={
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                <ContactReasons contactReasons={item?.contactsReason} />
                <RequirePermission
                  validatePermission={(account) => !!account?.hasContactEditionPermission}
                >
                  <>
                    {onEdit && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => onEdit?.(item)}
                        sx={{ display: 'none', marginLeft: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => onDelete?.(item)}
                        sx={{ display: 'none', marginLeft: 2 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </>
                </RequirePermission>
              </Stack>
            }
          >
            <ListItemAvatar
              sx={{
                border: '1px solid',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '46px',
                minWidth: '46px',
                mr: 2,
                color: 'primary.light',
              }}
            >
              <PersonOutline />
            </ListItemAvatar>
            <ListItemText
              primary={mountItemName(item)}
              primaryTypographyProps={{
                noWrap: true,
              }}
              secondary={mountItemDescription([
                {
                  value: item.phone?.trim(),
                  applyMask: maskPhone,
                },
                {
                  value: item.additionalPhone?.trim(),
                  applyMask: maskPhone,
                },
                {
                  value: item.email?.trim(),
                },
              ])}
            />
          </ListItem>
          {index < items.length - 1 && <Divider />}
        </Fragment>
      ))}
    </List>
  );
}

function ContactReasons({ contactReasons }: { contactReasons: UserAndContact['contactsReason'] }) {
  const { t } = useTranslation('_common');
  const items = React.useMemo(
    () => (contactReasons ? Object.values(contactReasons) : []),
    [contactReasons],
  );

  const icons = {
    Fire: <FireIcon />,
    Violation: <ViolationIcon />,
    Medical: <MedicalIcon />,
    Panic: <PanicIcon />,
  };

  return (
    <>
      {items
        .filter((item) => item.enabled)
        .map((item) => (
          <ContactReasonItem
            key={item.id}
            color={item.color}
            icon={icons[item.icon]}
            label={t(item.text)}
          />
        ))}
    </>
  );
}

function ContactReasonItem({
  color,
  label,
  icon,
}: {
  color: string;
  label: string;
  icon: React.ReactElement;
}) {
  return (
    <Chip
      icon={icon}
      label={label}
      sx={{
        ml: 1,
        /* @ts-ignore */
        color: colors[color][700],
        /* @ts-ignore */
        bgcolor: colors[color][50],
        '.MuiChip-icon': {
          /* @ts-ignore */
          color: colors[color][700],
        },
      }}
    />
  );
}

export default CommonList;
